import { html, SP } from '../html.js';
import type { VNode } from '../html.js';
import type { Actions } from '../actions.js';
import type { ConsoleState } from '../store.js';
import { elapsed } from '../store.js';
import { FailureNotice } from '../ui/failure.js';
import { Ledger } from '../ui/ledger.js';

/**
 * The crawl, live.
 *
 * Stage-by-stage from the SSE stream, with the probe stage expanded in place.
 * The progress bar is deliberately a 2px rule rather than a chunky widget: the
 * information the operator wants is in the ledger, and a big bar competing with
 * it would be the console lying about where to look.
 *
 * Only ONE region announces. The ledger used to sit inside `aria-live="polite"`,
 * which meant a screen reader read every log line of an eight-page crawl —
 * dozens of URLs — and buried the verdicts inside them. The live region is now
 * a single hidden sentence that names the stage and the running tally, and the
 * ledger itself is silent and navigable at the reader's own pace.
 */
export function ProgressScreen(props: {
  state: ConsoleState;
  actions: Actions;
  now: number;
}): VNode {
  const { state, actions, now } = props;
  const host = hostOf(state.targetUrl);
  const cleared = state.clearances.filter((c) => c.verdict === 'cleared').length;
  const turned = state.clearances.length - cleared;
  const clock = state.startedAt ? elapsed(state.startedAt, state.finishedAt ?? now) : '0:00';
  const active = state.stages.find((s) => s.status === 'running');

  const spoken = state.fatal
    ? `Crawl stopped. ${state.fatal}`
    : active
      ? `Stage ${active.ordinal} of 6, ${active.label}. ${state.pct}% complete.` +
        (state.clearances.length
          ? ` ${cleared} cleared, ${turned} turned back of ${Math.max(state.proposals.length, state.clearances.length)} declared.`
          : '')
      : 'Waiting for the first frame.';

  return html`
    <div class="wrap">
      <div class="runhead">
        <h1 class="runhead__target" tabindex="-1" data-focus-target>${host}</h1>
        <span class="runhead__meta">
          ${state.fatal ? 'stopped' : 'crawling'} · ${clock} elapsed · ${state.pct}%
          ${state.clearances.length ? ` · ${cleared} cleared, ${turned} turned back` : ''}
        </span>
      </div>

      <div
        class="meter"
        role="progressbar"
        aria-valuenow=${state.pct}
        aria-valuemin="0"
        aria-valuemax="100"
        aria-label="Crawl progress"
      >
        <div class="meter__fill" style=${`width:${state.pct}%`}></div>
      </div>

      <p class="sr" role="status">${spoken}</p>

      ${Ledger({
        stages: state.stages,
        proposals: state.proposals,
        clearances: state.clearances,
        now,
      })}

      ${state.fatal
        ? html`<div style="margin-top:28px">
            ${FailureNotice({ message: state.fatal, host, onRestart: () => actions.restart() })}
          </div>`
        : null}

      <p class="foot" style="border:0;padding-bottom:56px">
        ${state.fatal
          ? html`${host} was fetched read-only as ClawHarborBot/1.0 at one request per second,
              obeying robots.txt. Nothing behind a login was touched and nothing was written back.`
          : html`Fetching ${host} at one request per second as ClawHarborBot/1.0, obeying
              robots.txt. Nothing behind a login is touched and nothing is written back.`}${SP}
      </p>
    </div>
  `;
}

function hostOf(url: string | null): string {
  if (!url) return 'the site';
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
}
